import { Node, mergeAttributes } from '@tiptap/core';

/**
 * RatingScale Extension
 * Creates a 1-10 rating scale for self-assessment
 * Responses are stored in the database per user
 */
export const RatingScale = Node.create({
  name: 'ratingScale',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => ({
          'data-id': attributes.id,
        }),
      },
      prompt: {
        default: 'Rate your experience:',
        parseHTML: (element) => element.getAttribute('data-prompt'),
        renderHTML: (attributes) => ({
          'data-prompt': attributes.prompt,
        }),
      },
      minLabel: {
        default: '1 - Low',
        parseHTML: (element) => element.getAttribute('data-min-label'),
        renderHTML: (attributes) => ({
          'data-min-label': attributes.minLabel,
        }),
      },
      maxLabel: {
        default: '10 - High',
        parseHTML: (element) => element.getAttribute('data-max-label'),
        renderHTML: (attributes) => ({
          'data-max-label': attributes.maxLabel,
        }),
      },
      min: {
        default: 1,
        parseHTML: (element) => parseInt(element.getAttribute('data-min') || '1'),
        renderHTML: (attributes) => ({
          'data-min': attributes.min,
        }),
      },
      max: {
        default: 10,
        parseHTML: (element) => parseInt(element.getAttribute('data-max') || '10'),
        renderHTML: (attributes) => ({
          'data-max': attributes.max,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="rating-scale"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const id =
      node.attrs.id ||
      HTMLAttributes['data-id'] ||
      `rating-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const prompt = node.attrs.prompt || HTMLAttributes['data-prompt'] || 'Rate your experience:';
    const minLabel = node.attrs.minLabel || HTMLAttributes['data-min-label'] || '1 - Low';
    const maxLabel = node.attrs.maxLabel || HTMLAttributes['data-max-label'] || '10 - High';
    const min = node.attrs.min || parseInt(HTMLAttributes['data-min'] || '1');
    const max = node.attrs.max || parseInt(HTMLAttributes['data-max'] || '10');

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'rating-scale',
        'data-id': id,
        'data-prompt': prompt,
        'data-min-label': minLabel,
        'data-max-label': maxLabel,
        'data-min': min,
        'data-max': max,
        class: 'rating-scale-block',
      }),
      ['div', { class: 'rating-prompt' }, prompt],
      [
        'div',
        { class: 'rating-scale-container' },
        ['span', { class: 'rating-label rating-label-min' }, minLabel],
        [
          'div',
          {
            class: 'rating-buttons',
            'data-rating-id': id,
            'data-min': min,
            'data-max': max,
          },
          '1 2 3 4 5 6 7 8 9 10',
        ],
        ['span', { class: 'rating-label rating-label-max' }, maxLabel],
      ],
      [
        'div',
        { class: 'rating-reflection' },
        ['label', { class: 'rating-reflection-label' }, 'Reflect on your rating:'],
        [
          'textarea',
          {
            class: 'rating-comment',
            'data-rating-id': id,
            placeholder:
              'Why did you rate yourself this way? What can you do to continue developing your practice?',
            rows: 3,
          },
        ],
      ],
      [
        'div',
        { class: 'rating-footer' },
        ['span', { class: 'rating-status' }, 'Click a number to save your rating'],
      ],
    ];
  },

  // Render through a NodeView so ProseMirror treats the block as an opaque widget.
  // Like TextAreaBlock, this contains a live <textarea> (.rating-comment); without
  // this, Android soft-keyboard backspace/autocomplete mutations make ProseMirror
  // redraw the atom node and the comment box vanishes mid-typing. It also lets
  // course-view rewrite .rating-buttons into clickable buttons without ProseMirror
  // fighting that DOM mutation.
  addNodeView() {
    return ({ node }) => {
      const attrs = node.attrs || {};
      const id = attrs.id || `rating-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const prompt = attrs.prompt || 'Rate your experience:';
      const minLabel = attrs.minLabel || '1 - Low';
      const maxLabel = attrs.maxLabel || '10 - High';
      const min = attrs.min || 1;
      const max = attrs.max || 10;

      const dom = document.createElement('div');
      dom.className = 'rating-scale-block';
      dom.setAttribute('data-type', 'rating-scale');
      dom.setAttribute('data-id', id);
      dom.setAttribute('data-prompt', prompt);
      dom.setAttribute('data-min-label', minLabel);
      dom.setAttribute('data-max-label', maxLabel);
      dom.setAttribute('data-min', String(min));
      dom.setAttribute('data-max', String(max));

      const promptEl = document.createElement('div');
      promptEl.className = 'rating-prompt';
      promptEl.textContent = prompt;
      dom.appendChild(promptEl);

      const container = document.createElement('div');
      container.className = 'rating-scale-container';
      const minSpan = document.createElement('span');
      minSpan.className = 'rating-label rating-label-min';
      minSpan.textContent = minLabel;
      const buttons = document.createElement('div');
      buttons.className = 'rating-buttons';
      buttons.setAttribute('data-rating-id', id);
      buttons.setAttribute('data-min', String(min));
      buttons.setAttribute('data-max', String(max));
      buttons.textContent = '1 2 3 4 5 6 7 8 9 10';
      const maxSpan = document.createElement('span');
      maxSpan.className = 'rating-label rating-label-max';
      maxSpan.textContent = maxLabel;
      container.appendChild(minSpan);
      container.appendChild(buttons);
      container.appendChild(maxSpan);
      dom.appendChild(container);

      const reflection = document.createElement('div');
      reflection.className = 'rating-reflection';
      const label = document.createElement('label');
      label.className = 'rating-reflection-label';
      label.textContent = 'Reflect on your rating:';
      const textarea = document.createElement('textarea');
      textarea.className = 'rating-comment';
      textarea.setAttribute('data-rating-id', id);
      textarea.placeholder =
        'Why did you rate yourself this way? What can you do to continue developing your practice?';
      textarea.rows = 3;
      reflection.appendChild(label);
      reflection.appendChild(textarea);
      dom.appendChild(reflection);

      const footer = document.createElement('div');
      footer.className = 'rating-footer';
      const status = document.createElement('span');
      status.className = 'rating-status';
      status.textContent = 'Click a number to save your rating';
      footer.appendChild(status);
      dom.appendChild(footer);

      return {
        dom,
        ignoreMutation: () => true,
        stopEvent: () => true,
      };
    };
  },

  addCommands() {
    return {
      setRatingScale:
        (attributes) =>
        ({ commands }) => {
          const id = `rating-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return commands.insertContent({
            type: this.name,
            attrs: {
              id,
              ...attributes,
            },
          });
        },
    };
  },
});

export default RatingScale;
